// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// «HÁ QUANTO TEMPO» — uma só formulação, partilhada
// ============================================================================
//
// 📄 §11 linha O, peça **P1**. O cartão da célula e o painel do dia dizem «alterada há N min
//    por …», e o número sai daqui.
//
// ⚠️⚠️ **O CÁLCULO É NO ECRÃ E NÃO NO SERVIDOR, e a razão é medida.** O mapa é serializado
//    **uma vez** e servido de cache (3,24 MB → 0,51 MB gzip, ~15 ms nos pedidos seguintes):
//    um «há N min» calculado em SQL congelava no instante em que o blob foi construído, e o
//    blob pode ficar horas de pé entre duas mudanças do plano. 📌 *«Alterada há 3 min»* sobre
//    uma coisa de há duas horas é um valor plausível no lugar do certo — exactamente o modo
//    de falhar que esta peça existe para fechar.
//
//    O que viaja é o **instante absoluto** em ISO-8601 UTC; a subtracção é feita no momento de
//    desenhar. ⚠️ O preço está declarado: um relógio de cliente adiantado ou atrasado desloca
//    a frase. É o menor dos dois males, e é recuperável (basta recarregar); um contador
//    congelado não se distingue de um correcto.
//
// ⚠️ **NÃO É A `minutos()` DA PRESENÇA, e não é descuido.** `presenca.ts` e
//    `QuemEstaNoPlano.tsx` têm cada um a sua cópia privada de uma função `minutos()` que pára
//    nas horas — e pode fazê-lo, porque uma sessão não dura dias. Aqui **pode ter dias**: uma
//    linha modificada na semana passada tem de dizer «há 6 dias» e não «há 144 h».
//    🅿️ **Declarado e NÃO feito:** as duas cópias privadas são candidatas a convergir para
//    esta, que é a mais geral das três. Não o fiz porque são ficheiros fora do âmbito desta
//    peça e não estão avariados (regra 3) — fica registado para não nascer uma quarta.
// ============================================================================
/** Um minuto, em milissegundos. */
const MINUTO = 60_000;
/**
 * «há 3 min», «há 2 h», «há 6 dias» — ou `null` quando não há instante nenhum.
 *
 * ⚠️⚠️ **`null` CALA-SE, e é a decisão central desta peça.** Uma linha sem carimbo não é
 * «alterada há muito tempo» nem «nunca alterada»: é uma linha sobre a qual **não se sabe
 * nada**, porque a âncora só passou a existir com a migração 090. Devolver texto aqui era
 * desfazer, no ecrã, a razão de a coluna ser anulável.
 *
 * ⚠️ **Um instante no FUTURO lê-se «agora mesmo»** e não «há -3 min». Acontece por desvio de
 * relógio entre o servidor e o posto, e um número negativo numa frase de tempo faz quem o lê
 * duvidar do sistema inteiro em vez de duvidar do relógio.
 *
 * @param quando instante ISO-8601 (UTC), tal como o mapa o serviu; `null`/vazio se não houver
 * @param agora  instante de referência em milissegundos — explícito para as provas não
 *               dependerem do relógio da máquina onde correm
 */
export function haQuantoTempo(quando, agora) {
    if (quando === null || quando === undefined || quando === '')
        return null;
    const t = Date.parse(quando);
    if (Number.isNaN(t))
        return null;
    const min = Math.floor((agora - t) / MINUTO);
    if (min <= 0)
        return 'agora mesmo';
    if (min === 1)
        return 'há 1 min';
    if (min < 120)
        return `há ${String(min)} min`;
    const horas = Math.floor(min / 60);
    if (horas < 48)
        return `há ${String(horas)} h`;
    const dias = Math.floor(horas / 24);
    return dias === 1 ? 'há 1 dia' : `há ${String(dias)} dias`;
}
/**
 * A frase inteira do cartão: «alterada há 3 min por Ana Silva».
 *
 * ⚠️ **Sem nome, a frase encolhe em vez de inventar um.** `actualizado_por` fica `NULL` quando
 * a escrita veio de fora do caminho HTTP — uma ferramenta, a semeadura, uma migração. Escrever
 * «por sistema» dava um nome a uma coisa que não tem nome, e este projecto já pagou por
 * apresentar uma dedução como um registo.
 */
export function fraseDaAlteracao(alteracao, agora) {
    if (alteracao === undefined)
        return null;
    const quando = haQuantoTempo(alteracao[0], agora);
    if (quando === null)
        return null;
    const quem = alteracao[1];
    return quem === null || quem === '' ? `alterada ${quando}` : `alterada ${quando} por ${quem}`;
}
//# sourceMappingURL=tempo-relativo.js.map